import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getAuth, deleteUser, sendPasswordResetEmail } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import moment from 'moment';
import { db } from '../lib/firebase';
import '../styles/users.css';
import settingDP from '../images/fiat.png';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { FaPlusCircle } from 'react-icons/fa';
import Loader from '../components/Loader';

const UserInfo = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [updatedUser, setUpdatedUser] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [profilePictureUrl, setProfilePictureUrl] = useState('');
    const [uploadError, setUploadError] = useState('');
    const [balance, setBalance] = useState(0);
    const fileInputRef = useRef(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);


    useEffect(() => {
        // * Function to fetch user data from Firestore
        const fetchUser = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'bit-lite', userId));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setUser(userData);
                    setUpdatedUser(userData);
                    setProfilePictureUrl(userData.profilePictureUrl || '');

                    // Calculate the balance
                    const userTransactions = userData.transactions || [];
                    const userBalance = userTransactions.reduce((total, transaction) => {
                        return transaction.type === 'credit' ? total + transaction.amount : total - transaction.amount;
                    }, 0);
                    setBalance(userBalance);

                } else {
                    console.log('No such document!');
                }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching user: ', error);
                setLoading(false);
            }
        };

        fetchUser();
        // Fetch user data when userId changes
    }, [userId]);

    // * change value on input
    const handleChange = (e) => {
        const { name, value } = e.target;
        setUpdatedUser({ ...updatedUser, [name]: value });
    };

    // * handle saves after editing
    const handleSave = async () => {
        setIsSaving(true);
        try {
            await updateDoc(doc(db, 'bit-lite', userId), updatedUser);
            setUser(updatedUser);
            setEditMode(false);
            setSuccessMessage('Update successful!');
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            setUploadError(`Error saving: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    // * handle forgot password
    const handleForgotPassword = async () => {
        const auth = getAuth();

        if (!user || !user.email) {
            setUploadError('User email not found.');
            return;
        }

        try {
            await sendPasswordResetEmail(auth, user.email);
            setSuccessMessage('Password reset email sent successfully to ' + user.email);
            setTimeout(() => {
                setSuccessMessage('');
            }, 8000);
        } catch (error) {
            setUploadError(`Error sending password reset email: ${error.message}`);
            console.error('Error sending password reset email:', error.message);
        }
    };

 // * handle delete user
const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const auth = getAuth();
      
      // First delete the Firestore document
      await deleteDoc(doc(db, 'bit-lite', userId));
      
      // Then try to delete the auth user
      try {
        const currentUser = auth.currentUser;
        
        // If the user to delete is the currently signed in user
        if (currentUser && currentUser.uid === userId) {
          await deleteUser(currentUser);
        } else {
          // For other users, we can't delete them directly from client-side
          // You'll need to implement this on your backend
          console.log("Cannot delete auth user from client-side - implement backend deletion");
        }
      } catch (authError) {
        console.log('Auth user deletion error (might need backend implementation):', authError);
      }
      
      navigate('/get-admin');
    } catch (error) {
      console.error('Error deleting user: ', error);
      setUploadError(`Error deleting user: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

    // * state to handle the change of profile pic file
    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            handleUpload(e.target.files[0]);
        }
    };

    // * state to handle upload of profile pic
    const handleUpload = async (file) => {
        const storage = getStorage();
        // Create storage reference
        const storageRef = ref(storage, `profilePictures/${userId}/${file.name}`);

        try {
            // Upload profile picture and get download url
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            // Update the Firestore document with the new profile picture URL
            await updateDoc(doc(db, 'bit-lite', userId), { profilePictureUrl: downloadURL });
            setProfilePictureUrl(downloadURL);

            setSuccessMessage('Profile picture uploaded successfully.');
            setTimeout(() => {
                setSuccessMessage('');
            }, 5000);
        } catch (error) {
            setUploadError(`Error uploading profile picture: ${error.message}`);
            console.error('Error uploading profile picture:', error.message);
        }
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    if (loading) {
        return <div><Loader /></div>;
    }

    if (!user) {
        return <div>User not found</div>;
    }

    return (
        <div className='pager'>
            <div className="user-infos">
                {/* head section of user details */}
                <div className="user-info-container">
                    {/* profile image selector and display */}
                    <div className="user-info-item">
                        <div className="dpClass">
                            <div className='profile-picture-container'>
                                <div className='profile-picture-wrapper'>
                                    <img
                                        src={profilePictureUrl || settingDP}
                                        alt="Profile"
                                        className="profile-picture"
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                        className="file-input"
                                        style={{ display: 'none' }}
                                    />
                                    <FaPlusCircle
                                        className="upload-icon"
                                        onClick={() => fileInputRef.current.click()}
                                    />
                                </div>
                                {uploadError && <p className="error-message">{uploadError}</p>}
                            </div>
                        </div>
                    </div>
    
                    {/* basic user details */}
                    <div className="user-info-item">
                        <h2><b className="usernames">Full Name:</b> {user.fullName}</h2>
                        <div>
                            <b className="username">Email: </b>
                            {user.email}{' '}
                            <i onClick={handleForgotPassword} className="reset fas fa-repeat"></i>
                        </div>
                        <div className='usernames'>
                            <><b className="">Account Balance:</b> ${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
                        </div>
                        <div className='username'>
                            <Link
                                className='reset'
                                to={`/userdetail/${userId}/transactions`}>
                                View Transaction Log</Link>
                        </div>
                    </div>
                </div>
    
    
                {/* User Full Information */}
                <div>
                    <h3 className='username'>User Details</h3>
                    <>
                        {/* Account Number and Address */}
                        <div className="user-info-container">
                            <div className="user-info-item">
                                {editMode ? (
                                    <><div><b className="usernames">Full Name:</b></div>
                                        <input
                                            type="text"
                                            name="fullName"
                                            className="input-field"
                                            value={updatedUser.fullName}
                                            onChange={handleChange}
                                        /></>
                                ) : (
                                    <><b className="usernames">Full Name:</b> {user.fullName}</>
                                )}
                            </div>
                            <div className="user-info-item">
                                {editMode ? (
                                    <>
                                        <b className="username">Address:</b>
                                        <input
                                            type="text"
                                            name="address"
                                            className="input-field"
                                            value={updatedUser.address}
                                            onChange={handleChange}
                                        />
                                    </>
                                ) : (
                                    <><b className="username">Address:</b> {user.address}</>
                                )}
                            </div>
                        </div>
    
    
                        {/* Gender and Date of Birth */}
                        <div className="user-info-container">
                            <div className="user-info-item">
                                {editMode ? (
                                    <>
                                        <b className="username">Gender:</b>
                                        <select
                                            name="gender"
                                            value={updatedUser.gender}
                                            onChange={handleChange}
                                        >
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                        </select>
                                    </>
                                ) : (
                                    <><b className="username">Gender:</b> {user.gender}</>
                                )}
                            </div>
                            <div className="user-info-item">
                                {editMode ? (
                                    <>
                                        <div> <b className="usernames">Date of Birth:</b></div>
                                        <input
                                            type="date"
                                            name="dob"
                                            className="input-field"
                                            value={moment(updatedUser.dob).format('YYYY-MM-DD')}
                                            onChange={handleChange}
                                        />
                                    </>
                                ) : (
                                    <><b className="usernames">Date of Birth:</b> {moment(user.dob).format('MMMM D, YYYY')}</>
                                )}
                            </div>
                        </div>
    
    
                        {/* Role and Special Code */}
                        <div className="user-info-container">
                            <div className="user-info-item">
                                {editMode ? (
                                    <>
                                        <b className="username">Role:</b>
                                        <select
                                            name="role"
                                            value={updatedUser.role}
                                            onChange={handleChange}
                                        >
                                            <option value="user">Regular User</option>
                                            <option value="investor">Mega FXT Investor</option>
                                            <option value="admin">Global Admin</option>
                                        </select>
                                    </>
                                ) : (
                                    <><b className="username">Role:</b> {user.role}</>
                                )}
                            </div>
                            <div className="user-info-item">
                                {editMode ? (
                                    <><div><b className="usernames">Special Code:</b></div>
                                        <input
                                            type="text"
                                            name="code"
                                            className="input-field"
                                            value={updatedUser.code}
                                            onChange={handleChange}
                                        /></>
                                ) : (
                                    <><b className="usernames">Special Code:</b> {user.code || 'N/A'}</>
                                )}
                            </div>
                        </div>
    
    
                        {/* Status and Transaction Report */}
                        <div className="user-info-container">
                            <div className="user-info-item">
                                {editMode ? (
                                    <>
                                        <b className="username">Group Status:</b>
                                        <select
                                            name="group"
                                            value={updatedUser.group}
                                            onChange={handleChange}
                                        >
                                            <option value="activated">Activated</option>
                                            <option value="cancelled">Cancelled</option>
                                            <option value="deactivated">Deactivated</option>
                                        </select>
                                    </>
                                ) : (
                                    <><b className="username">Group Status:</b> {user.group}</>
                                )}
                            </div>
                            <div className="user-info-item">
                                {editMode ? (
                                    <><div><b className="usernames">Transaction Report:</b></div>
                                        <input
                                            type="text"
                                            name="report"
                                            className="input-field"
                                            value={updatedUser.report || "No reports available"}
                                            onChange={handleChange}
                                        /></>
                                ) : (
                                    <><b className="usernames">Transaction Report:</b> {user.report || 'N/A'}</>
                                )}
                            </div>
                        </div>
    
                    </>
    
    
    
                    <>
                        {editMode ? (
                            <button
                                onClick={handleSave}
                                className='click-button'
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i> Saving...
                                    </>
                                ) : 'Save Changes'}
                            </button>
                        ) : (
                            <button onClick={() => setEditMode(true)} className='click-button'>
                                Edit Info
                            </button>
                        )}
    
                        <button
                            onClick={handleDelete}
                            className="delete-button"
                            disabled={isDeleting || isSaving}
                        >
                            {isDeleting ? 'Deleting ...' : 'Delete User'}
                        </button>
                    </>
                    {successMessage && <p></p>}
    
    
                    <div className="center">
                        <button onClick={handleGoBack} className='click-button'>Go Back</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserInfo;
